/**
 * Smoke from under a crashed vehicle's bonnet: pale steam from a split
 * radiator at first, dark oil smoke from an engine badly hurt.
 *
 * Puffs are faceted balls in one instanced mesh, as the world is drawn:
 * each rises, drifts with the wind, swells and thins away over two or three
 * seconds. How many a vehicle gives off follows its handling's `smoke`
 * (`core/vehicle/handling.ts`).
 */

import { Color, DynamicDrawUsage, IcosahedronGeometry, InstancedMesh, Matrix4, MeshStandardMaterial, Quaternion, Vector3 } from 'three';

/** Most puffs in the air at once. */
const MOST = 240;

interface Puff {
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  age: number; life: number;
  dark: number;
}

const matrix = new Matrix4();
const at = new Vector3();
const turn = new Quaternion();
const size = new Vector3();
const colour = new Color();
const STEAM = new Color(0xe6e8ea), OIL = new Color(0x2b2b2d);

export class Smoke {
  readonly mesh: InstancedMesh;
  private readonly puffs: Puff[] = [];
  /** Puffs owed to each source, carried over between frames. */
  private readonly owed = new Map<object, number>();

  constructor() {
    const material = new MeshStandardMaterial({ color: 0xffffff, roughness: 1, flatShading: true, transparent: true, opacity: 0.55, depthWrite: false });
    this.mesh = new InstancedMesh(new IcosahedronGeometry(1, 0), material, MOST);
    this.mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
  }

  /**
   * Give off smoke from scene (x, y, z) for `dt` seconds at `amount`, 0 to 1,
   * for `source`, which keeps its own count of puffs owed between frames.
   */
  emit(source: object, x: number, y: number, z: number, amount: number, dt: number, wind: { x: number; z: number }): void {
    if (amount <= 0) return;
    let owed = (this.owed.get(source) ?? 0) + dt * (3 + amount * 16);
    while (owed >= 1 && this.puffs.length < MOST) {
      owed -= 1;
      this.puffs.push({
        x: x + (Math.random() - 0.5) * 0.4, y, z: z + (Math.random() - 0.5) * 0.4,
        vx: wind.x * 0.5 + (Math.random() - 0.5) * 0.3, vy: 0.7 + Math.random() * 0.6, vz: wind.z * 0.5 + (Math.random() - 0.5) * 0.3,
        age: 0, life: 2 + Math.random() * 1.2,
        dark: Math.max(0, Math.min(1, (amount - 0.55) / 0.35)),
      });
    }
    this.owed.set(source, owed);
  }

  update(dt: number): void {
    let n = 0;
    for (let i = this.puffs.length - 1; i >= 0; i--) {
      const p = this.puffs[i];
      p.age += dt;
      if (p.age >= p.life) {
        this.puffs.splice(i, 1);
        continue;
      }
      p.x += p.vx * dt; p.y += p.vy * dt; p.z += p.vz * dt;
      p.vy *= Math.exp(-dt * 0.4);
      const t = p.age / p.life;
      // Swelling as it rises, and thinning to nothing at the end.
      const r = (0.15 + t * 0.85) * (1 - t * t * t);
      at.set(p.x, p.y, p.z);
      turn.setFromAxisAngle(at.set(0, 1, 0), p.age * 0.6 + i);
      at.set(p.x, p.y, p.z);
      size.setScalar(r);
      matrix.compose(at, turn, size);
      this.mesh.setMatrixAt(n, matrix);
      this.mesh.setColorAt(n, colour.copy(STEAM).lerp(OIL, p.dark));
      n++;
    }
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor !== null) this.mesh.instanceColor.needsUpdate = true;
  }
}
